import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Lead, PaginatedResponse, User, DealStageValue, ReachabilityValue, RelationshipValue } from '../../types';
import { fetchLeads, deleteLead, fetchUsersList, fetchLeadSources, importLeadsCsv, assignLead, autoAssignLead } from '../../services/api';
import { useSuperTenant } from '../../context/SuperAdminTenantContext';
import { useAuth } from '../../context/AuthContext';
import { encodeId } from '../../utils/hashids';
import { whatsappVendorChatUrl } from '../../utils/whatsappVendor';
import LeadStatusBadge, { STATUS_LABELS as DEAL_STAGE_LABELS } from './LeadStatusBadge';
import { REACHABILITY_LABELS } from './ReachabilityBadge';
import { RELATIONSHIP_LABELS } from './RelationshipBadge';
import Pager from '../Pager';

interface ImportResult { imported: number; skipped: number; errors: string[]; }

const SOURCES = ['Manual', 'Meta', 'Google', 'IndiaMart', 'JustDial', 'Referral', 'WhatsApp', 'Website', 'Inbound', 'Other'];
const STATUSES = ['new', 'ai_contacted', 'qualified', 'human_called', 'proposal', 'won', 'lost', 'nurture'];
const TEMPS = ['COLD', 'WARM', 'HOT'];

// Deal-stage axis (Phase 4, four-axis lead status model, 2026-09-20)
const DEAL_STAGE_VALUES: DealStageValue[] = [
  'new', 'contacted', 'qualified', 'quoted',
  'negotiation', 'nurture', 'won', 'lost',
];

// Reachability axis (Phase 1, four-axis lead status model, 2026-09-20)
const REACHABILITY_VALUES: ReachabilityValue[] = [
  'unattempted', 'attempted', 'unreached', 'reached', 'do_not_call',
];

// Relationship axis (Phase 2, four-axis lead status model, 2026-09-20)
const RELATIONSHIP_VALUES: RelationshipValue[] = ['prospect', 'customer', 'churned'];

// Issues axis taxonomy (Phase 3, four-axis lead status model, 2026-09-20).
// Matches resources/skills/extract-objections.md's category list.
const ISSUE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'price_high_vs_competitor',   label: 'Price high vs competitor' },
  { value: 'price_increase_on_existing', label: 'Price increase (existing customer)' },
  { value: 'mrp_too_low',                label: 'MRP too low' },
  { value: 'margin_realization',         label: 'Margin realization' },
  { value: 'moq_too_high',               label: 'MOQ too high' },
  { value: 'payment_terms',              label: 'Payment terms' },
  { value: 'product_not_available',      label: 'Product not available' },
  { value: 'delivery_delay',             label: 'Delivery delay' },
  { value: 'stock_not_moving',           label: 'Stock not moving' },
  { value: 'product_defect',             label: 'Product defect' },
  { value: 'quality_concern',            label: 'Quality concern' },
  { value: 'monopoly_breach',            label: 'Monopoly breach' },
  { value: 'area_occupied',              label: 'Area occupied' },
  { value: 'agreement_terms',            label: 'Agreement terms' },
  { value: 'trust_deficit_prior_vendor', label: 'Trust deficit (prior vendor)' },
  { value: 'profitability_doubt',        label: 'Profitability doubt' },
  { value: 'doctor_not_prescribing',     label: 'Doctor not prescribing' },
  { value: 'call_fatigue',               label: 'Call fatigue' },
  { value: 'escalation_requested',       label: 'Escalation requested' },
];

const STATUS_LABELS: Record<string, string> = {
  new: 'New', ai_contacted: 'AI Contacted', qualified: 'Qualified',
  human_called: 'Human Called', proposal: 'Proposal', won: 'Won',
  lost: 'Lost', nurture: 'Nurture',
};

const statusClass: Record<string, string> = {
  new: 'badge-new', ai_contacted: 'badge-ai_contacted', qualified: 'badge-qualified',
  human_called: 'badge-human_called', proposal: 'badge-proposal',
  won: 'badge-won', lost: 'badge-lost', nurture: 'badge-nurture',
};

const sourceClass: Record<string, string> = {
  meta: 'source-meta', google: 'source-google', indiamart: 'source-indiamart',
  justdial: 'source-justdial', referral: 'source-referral', manual: 'source-manual',
  inbound: 'source-inbound',
};

const tempClass: Record<string, string> = {
  HOT: 'temp-hot', WARM: 'temp-warm', COLD: 'temp-cold',
};

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const wks = Math.floor(days / 7);
  return `${wks}w ago`;
}

function ScoreBar({ score = 0, max = 4 }: { score?: number; max?: number }) {
  return (
    <div className="score-bar-wrap">
      <div className="score-bar-track">
        {Array.from({ length: max }).map((_, i) => (
          <div key={i} className={`score-bar-seg${i < score ? ' score-bar-seg-filled' : ''}`} />
        ))}
      </div>
      <span className="score-bar-label">{score}/{max}</span>
    </div>
  );
}

/**
 * Count of BANT qualification flags currently confirmed on the lead.
 * Matches the Qualification Score widget on the Lead Detail page so
 * the list and detail views agree on the same number.
 */
function bantCount(l: Lead): number {
  return (
    (l.budget_confirmed ? 1 : 0) +
    (l.decision_maker_reached ? 1 : 0) +
    (l.timeline_stated ? 1 : 0) +
    (l.product_interest_confirmed ? 1 : 0)
  );
}

const LeadList: React.FC = () => {
  const history = useHistory();
  const { tenantId } = useSuperTenant();
  const { user } = useAuth();
  // Read-only external accounts hide every mutation surface — no Import,
  // no New Lead, no Edit / Call / Delete on rows. Backend also 403s these,
  // but hiding the buttons prevents confusing error toasts on click.
  const isReadOnly = user?.role === 'external_user';
  const [data, setData]              = useState<PaginatedResponse<Lead> | null>(null);
  const [loading, setLoading]        = useState(true);
  const [error, setError]            = useState<string | null>(null);
  // Sort state. Empty sortBy = backend default (created_at desc).
  const [sortBy, setSortBy]          = useState<string>('');
  const [sortDir, setSortDir]        = useState<'asc' | 'desc'>('desc');
  const [search, setSearch]          = useState('');
  const [sourceFilter, setSource]    = useState('');
  const [statusFilter, setStatus]    = useState('');
  const [dealStageFilter, setDealStage] = useState<DealStageValue | ''>('');
  const [reachabilityFilter, setReachability] = useState<ReachabilityValue | ''>('');
  const [relationshipFilter, setRelationship] = useState<RelationshipValue | ''>('');
  const [issueFilter, setIssueFilter] = useState('');
  const [tempFilter, setTemp]        = useState('');
  const [agentFilter, setAgent]      = useState('');
  const [dateFrom, setDateFrom]      = useState('');
  const [dateTo, setDateTo]          = useState('');
  const [page, setPage]              = useState(1);
  const [agents, setAgents]          = useState<User[]>([]);
  const [sourceOptions, setSourceOptions] = useState<Array<{ source: string; count: number }>>([]);

  // Bulk selection state
  const [selected, setSelected]      = useState<Set<number>>(new Set());
  const [bulkAgent, setBulkAgent]    = useState('');
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkResult, setBulkResult]  = useState<string | null>(null);

  // CSV import state
  const [showImport, setShowImport]     = useState(false);
  const [importFile, setImportFile]     = useState<File | null>(null);
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchUsersList().then(setAgents).catch(() => {});
  }, []);

  // Reload the source dropdown when the super-admin tenant filter changes —
  // different tenants have different source distributions.
  useEffect(() => {
    fetchLeadSources().then(setSourceOptions).catch(() => setSourceOptions([]));
  }, [tenantId]);

  const load = useCallback(async (silent = false) => {
    if (!silent) { setLoading(true); setError(null); }
    try {
      const res = await fetchLeads({
        page,
        search: search || undefined,
        status: statusFilter || undefined,
        deal_stage: dealStageFilter || undefined,
        reachability: reachabilityFilter || undefined,
        relationship: relationshipFilter || undefined,
        issue_category: issueFilter || undefined,
        source: sourceFilter || undefined,
        temperature: tempFilter || undefined,
        assigned_to: agentFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        tenant_id: tenantId || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortBy ? sortDir : undefined,
      });
      setData(res);
    } catch { if (!silent) setError('Failed to fetch leads'); }
    finally { if (!silent) setLoading(false); }
  }, [page, search, statusFilter, dealStageFilter, reachabilityFilter, relationshipFilter, issueFilter, sourceFilter, tempFilter, agentFilter, dateFrom, dateTo, tenantId, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const interval = setInterval(() => { load(true); }, 10000);
    return () => clearInterval(interval);
  }, [load]);
  useEffect(() => { setPage(1); }, [search, statusFilter, dealStageFilter, reachabilityFilter, relationshipFilter, issueFilter, sourceFilter, tempFilter, agentFilter, dateFrom, dateTo, sortBy, sortDir]);

  // Click a column header to sort. Same column again → flip asc/desc.
  // Different column → start descending (fits "latest activity first" muscle memory).
  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const sortArrow = (col: string) =>
    sortBy !== col ? '' : sortDir === 'asc' ? ' ▲' : ' ▼';

  const handleExportCsv = () => {
    const params = new URLSearchParams();
    if (search)           params.set('search', search);
    if (statusFilter)     params.set('status', statusFilter);
    if (dealStageFilter)  params.set('deal_stage', dealStageFilter);
    if (reachabilityFilter) params.set('reachability', reachabilityFilter);
    if (relationshipFilter) params.set('relationship', relationshipFilter);
    if (issueFilter)      params.set('issue_category', issueFilter);
    if (sourceFilter)     params.set('source', sourceFilter);
    if (tempFilter)       params.set('temperature', tempFilter);
    if (agentFilter)      params.set('assigned_to', agentFilter);
    if (dateFrom)         params.set('date_from', dateFrom);
    if (dateTo)           params.set('date_to', dateTo);
    if (tenantId)         params.set('tenant_id', String(tenantId));
    const token = localStorage.getItem('crm_token') ?? '';
    const base  = process.env.REACT_APP_API_URL ?? 'http://localhost:8001/api';
    // token is required — server responds 401 without it. Use fetch → blob →
    // trigger download so we can attach the Authorization header (a plain <a>
    // href would not carry it).
    fetch(`${base}/leads/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `leads_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => alert('CSV export failed'));
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this lead?')) return;
    try { await deleteLead(id); load(); }
    catch { alert('Failed to delete lead'); }
  };

  // CSV Import
  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportFile(e.target.files?.[0] ?? null);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const res = await importLeadsCsv(importFile);
      setImportResult(res);
      if (res.imported > 0) load();
    } catch {
      setImportResult({ imported: 0, skipped: 0, errors: ['Import failed. Check file format.'] });
    } finally {
      setImporting(false);
    }
  };

  const closeImport = () => {
    setShowImport(false);
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleSelect = (id: number) =>
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleAll = () => {
    const ids = data?.data.map(l => l.id) ?? [];
    setSelected(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };

  const handleBulkAssign = async () => {
    if (!bulkAgent || selected.size === 0) return;
    setBulkWorking(true);
    setBulkResult(null);
    let ok = 0;
    for (const id of Array.from(selected)) {
      try { await assignLead(id, parseInt(bulkAgent, 10)); ok++; }
      catch {}
    }
    setBulkResult(`Assigned ${ok}/${selected.size} leads.`);
    setSelected(new Set());
    setBulkAgent('');
    setBulkWorking(false);
    load();
  };

  const handleBulkAutoAssign = async () => {
    if (selected.size === 0) return;
    setBulkWorking(true);
    setBulkResult(null);
    let ok = 0;
    for (const id of Array.from(selected)) {
      try { await autoAssignLead(id); ok++; }
      catch {}
    }
    setBulkResult(`Auto-assigned ${ok}/${selected.size} leads.`);
    setSelected(new Set());
    setBulkWorking(false);
    load();
  };

  const hotCount = data?.data.filter(l => l.temperature === 'HOT').length ?? 0;

  return (
    <div className="list-page">
      {/* ── Header ── */}
      <div className="page-hd">
        <div className="page-hd-left" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 className="page-title">Leads</h1>
          {hotCount > 0 && (
            <button className="calling-queue-btn">
              <span className="calling-queue-dot" />
              Calling Queue <span className="calling-queue-count">{hotCount}</span>
            </button>
          )}
        </div>
        <div className="page-hd-right">
          {!isReadOnly && (
            <>
              <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Import CSV
              </button>
              <button className="btn btn-primary" onClick={() => history.push('/leads/new')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                + New Lead
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="filter-bar">
        <div className="filter-input-wrap">
          <svg className="filter-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input className="filter-input" placeholder="Search by name or mobile…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="filter-select" value={sourceFilter} onChange={e => setSource(e.target.value)}>
          <option value="">All sources</option>
          {(sourceOptions.length > 0
            ? sourceOptions.map(s => ({ value: s.source, label: `${s.source} (${s.count})` }))
            : SOURCES.map(s => ({ value: s.toLowerCase(), label: s }))
          ).map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select className="filter-select" value={statusFilter} onChange={e => setStatus(e.target.value)}>
          <option value="">All pipeline stages</option>
          {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select
          className="filter-select"
          value={dealStageFilter}
          onChange={e => setDealStage(e.target.value as DealStageValue | '')}
        >
          <option value="">All deal stages</option>
          {DEAL_STAGE_VALUES.map(s => (
            <option key={s} value={s}>{DEAL_STAGE_LABELS[s]}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={reachabilityFilter}
          onChange={e => setReachability(e.target.value as ReachabilityValue | '')}
        >
          <option value="">All reachability</option>
          {REACHABILITY_VALUES.map(r => (
            <option key={r} value={r}>{REACHABILITY_LABELS[r]}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={relationshipFilter}
          onChange={e => setRelationship(e.target.value as RelationshipValue | '')}
        >
          <option value="">All relationships</option>
          {RELATIONSHIP_VALUES.map(r => (
            <option key={r} value={r}>{RELATIONSHIP_LABELS[r]}</option>
          ))}
        </select>
        <select className="filter-select" value={issueFilter} onChange={e => setIssueFilter(e.target.value)}>
          <option value="">All issues</option>
          {ISSUE_CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select className="filter-select" value={tempFilter} onChange={e => setTemp(e.target.value)}>
          <option value="">All temps</option>
          {TEMPS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="filter-select" value={agentFilter} onChange={e => setAgent(e.target.value)}>
          <option value="">All agents</option>
          {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <span style={{ color: 'var(--text-muted)' }}>Created</span>
          <input
            type="date"
            className="filter-select"
            style={{ padding: '5px 8px', fontSize: 12, width: 140 }}
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            title="From date (created_at ≥)"
          />
          <span style={{ color: 'var(--text-muted)' }}>–</span>
          <input
            type="date"
            className="filter-select"
            style={{ padding: '5px 8px', fontSize: 12, width: 140 }}
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            title="To date (created_at ≤)"
          />
          {(dateFrom || dateTo) && (
            <button
              className="btn btn-ghost"
              style={{ fontSize: 11, padding: '4px 8px' }}
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              title="Clear date range"
            >
              ×
            </button>
          )}
        </div>
        <button
          className="btn btn-ghost"
          style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 12px' }}
          onClick={handleExportCsv}
        >
          Export CSV
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Bulk action bar ── */}
      {selected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 12,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{selected.size} selected</span>
          <select
            className="filter-select"
            style={{ flex: 1, maxWidth: 200 }}
            value={bulkAgent}
            onChange={e => setBulkAgent(e.target.value)}
          >
            <option value="">— assign to —</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button
            className="btn btn-primary"
            style={{ fontSize: 12, padding: '5px 14px' }}
            disabled={!bulkAgent || bulkWorking}
            onClick={handleBulkAssign}
          >
            {bulkWorking ? '…' : 'Assign'}
          </button>
          <button
            className="btn btn-secondary"
            style={{ fontSize: 12, padding: '5px 14px' }}
            disabled={bulkWorking}
            onClick={handleBulkAutoAssign}
          >
            {bulkWorking ? '…' : 'Auto-Assign'}
          </button>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, padding: '5px 10px' }}
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
          {bulkResult && <span style={{ fontSize: 12, color: '#166534' }}>{bulkResult}</span>}
        </div>
      )}

      {/* ── Table card ── */}
      <div className="data-card">
        {loading ? (
          <div className="spinner">Loading…</div>
        ) : (
          <>
            <div className="data-card-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={!!data && data.data.length > 0 && selected.size === data.data.length}
                        onChange={toggleAll}
                      />
                    </th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Sort by name">Name{sortArrow('name')}</th>
                    <th onClick={() => handleSort('mobile')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Sort by mobile">Mobile{sortArrow('mobile')}</th>
                    <th onClick={() => handleSort('source')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Sort by source">Source{sortArrow('source')}</th>
                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Sort by pipeline status">Status{sortArrow('status')}</th>
                    <th onClick={() => handleSort('deal_stage')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Sort by deal stage">Deal Stage{sortArrow('deal_stage')}</th>
                    <th onClick={() => handleSort('temperature')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Sort by temperature">Temp{sortArrow('temperature')}</th>
                    <th>Score</th>
                    <th>Assigned</th>
                    <th onClick={() => handleSort('last_touch_at')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Sort by last touch — most recent activity first">Last Touch{sortArrow('last_touch_at')}</th>
                    <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer', userSelect: 'none' }} title="Sort by created date">Created{sortArrow('created_at')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data && data.data.length > 0 ? data.data.map(l => (
                    <tr key={l.id}>
                      <td style={{ width: 32 }}>
                        <input
                          type="checkbox"
                          checked={selected.has(l.id)}
                          onChange={() => toggleSelect(l.id)}
                        />
                      </td>
                      {/* Name + company */}
                      <td>
                        <div className="avatar-chip">
                          <span className="avatar-sm avatar-sm-lead">{initials(l.name)}</span>
                          <div>
                            <div className="avatar-sm-name">{l.name}</div>
                            {l.company && <div className="td-muted" style={{ fontSize: 11 }}>{l.company}</div>}
                          </div>
                        </div>
                      </td>

                      {/* Mobile + whatsapp icon */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 13, color: 'var(--primary)' }}>{l.mobile || l.phone || '—'}</span>
                          {(l.whatsapp_number || l.mobile) && (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#25D366' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                          )}
                        </div>
                      </td>

                      {/* Source badge */}
                      <td>
                        <span className={`badge ${sourceClass[l.source?.toLowerCase()] ?? 'source-manual'}`}>
                          {l.source ? l.source.charAt(0).toUpperCase() + l.source.slice(1) : '—'}
                        </span>
                      </td>

                      {/* Pipeline stage */}
                      <td>
                        <span className={`badge ${statusClass[l.status] ?? 'badge-new'}`}>
                          {STATUS_LABELS[l.status] ?? l.status}
                        </span>
                      </td>

                      {/* Deal stage (Phase 4, four-axis lead status model) */}
                      <td>
                        <LeadStatusBadge
                          status={(l.deal_stage ?? 'new') as DealStageValue}
                          lostReason={l.lost_reason ?? null}
                          compact
                        />
                      </td>

                      {/* Temperature */}
                      <td>
                        {l.temperature
                          ? <span className={`badge ${tempClass[l.temperature]}`}>{l.temperature}</span>
                          : <span className="badge temp-cold">COLD</span>}
                      </td>

                      {/* BANT progress — count of the 4 qualification signals
                          confirmed on the lead (matches the "Qualification
                          Score N/4" widget on the Lead Detail page). Falls
                          back to the legacy `score` field for old records
                          that never went through the timeline analyzer. */}
                      <td><ScoreBar score={bantCount(l) || l.score || 0} max={4} /></td>

                      {/* Assigned */}
                      <td>
                        {l.assigned_user
                          ? (
                            <div className="avatar-chip">
                              <span className="avatar-sm" style={{ width: 22, height: 22, fontSize: 10 }}>
                                {initials(l.assigned_user.name)}
                              </span>
                              <span className="td-muted" style={{ fontSize: 12 }}>{l.assigned_user.name}</span>
                            </div>
                          )
                          : <span className="td-muted">Unassigned</span>}
                      </td>

                      {/* Last Touch */}
                      <td className="td-muted" style={{ fontSize: 12 }}>
                        {timeAgo(l.last_touch_at || l.updated_at)}
                      </td>

                      {/* Created — absolute IST datetime, small so it fits the row */}
                      <td className="td-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        {l.created_at ? new Date(l.created_at).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                          timeZone: 'Asia/Kolkata',
                        }) : '—'}
                      </td>

                      {/* Actions — View is always allowed; Edit/Call/Delete
                          hidden for read-only external accounts. */}
                      <td>
                        <div className="row-actions">
                          <button className="row-btn row-btn-view" title="View" onClick={() => history.push(`/leads/${encodeId(l.id)}`)}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          {/* Open the matching chat in the console.automately.net
                              vendor UI. Only renders for tenant 15 + source=whatsapp;
                              null URL hides the button for everything else. */}
                          {(() => {
                            const vendorUrl = whatsappVendorChatUrl(l);
                            return vendorUrl ? (
                              <button
                                className="row-btn"
                                title="Open WhatsApp chat"
                                style={{ color: '#25D366' }}
                                onClick={() => window.open(vendorUrl, '_blank', 'noopener,noreferrer')}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.18-.01-.382-.012-.57-.012-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                              </button>
                            ) : null;
                          })()}
                          {!isReadOnly && (
                            <>
                              <button className="row-btn" title="Edit" onClick={() => history.push(`/leads/${encodeId(l.id)}/edit`)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                              </button>
                              <button className="row-btn" title="Call" style={{ color: 'var(--teal)' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.64a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 18z"/></svg>
                              </button>
                              <button className="row-btn row-btn-danger" title="Delete" onClick={() => handleDelete(l.id)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr className="table-empty-row">
                      <td colSpan={12}>No leads found. Click <strong>+ New Lead</strong> to get started.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {data && <Pager currentPage={page} lastPage={data.last_page} total={data.total} label="Leads" onChange={setPage} />}
          </>
        )}
      </div>

      {/* CSV Import modal */}
      {showImport && (
        <div className="modal-overlay" onClick={closeImport}>
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Import Leads from CSV</h2>
              <button className="modal-close" onClick={closeImport}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#475569' }}>
                <strong>Required columns:</strong> <code>name</code>, <code>mobile</code><br />
                <strong>Optional:</strong> email, phone, whatsapp_number, company, city, district, state, business_type, drug_licence_number, gst_number, source, notes
              </div>

              <label className="form-label">Select CSV file</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleImportFile}
                style={{ display: 'block', marginBottom: 12, fontSize: 13 }}
              />

              {importResult && (
                <div style={{
                  background: importResult.errors.length > 0 ? '#fef9c3' : '#dcfce7',
                  border: `1px solid ${importResult.errors.length > 0 ? '#fde68a' : '#86efac'}`,
                  borderRadius: 8, padding: '12px 16px', marginBottom: 12, fontSize: 13,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Imported {importResult.imported} &middot; Skipped {importResult.skipped}
                  </div>
                  {importResult.errors.map((e, i) => (
                    <div key={i} style={{ color: '#92400e', fontSize: 12 }}>{e}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeImport}>
                {importResult ? 'Close' : 'Cancel'}
              </button>
              {!importResult && (
                <button
                  className="btn btn-primary"
                  disabled={!importFile || importing}
                  onClick={handleImport}
                >
                  {importing ? 'Importing…' : 'Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadList;
